import { apiClient, authenticatedFetch } from '../../services/api'

export interface AgenticChatTurn {
  role: 'user' | 'ai'
  text: string
}

export type AgenticRuntime = 'auto' | 'agent8' | 'hermes'

export interface AgenticChatResponse {
  reply: string
  provider: string
  model: string
  conversation_id: string
  title: string
}

export interface AgenticChatStreamResult {
  reply: string
  conversation_id: string
  title: string
  provider: string
  model: string
}

type AgenticChatStreamEvent =
  | { type: 'delta'; text: string }
  | { type: 'thinking'; text: string }
  | { type: 'tool'; name: string; label: string }
  | ({ type: 'done' } & AgenticChatStreamResult)
  | { type: 'error'; code: string; message: string }

export interface AgenticStreamHandlers {
  onDelta: (delta: string) => void
  onThinking?: (delta: string) => void
  onTool?: (tool: { name: string; label: string }) => void
}

export interface AgenticConversationSummary {
  id: string
  title: string
  created_at: string
  updated_at: string
}

export interface AgenticToolStep {
  name: string
  label: string
}

export interface AgenticMessage extends AgenticChatTurn {
  id: string
  provider: string | null
  model: string | null
  thinking: string | null
  tools: AgenticToolStep[] | null
  created_at: string
}

export interface AgenticConversation extends AgenticConversationSummary {
  messages: AgenticMessage[]
}

export type AgenticDocumentFormat = 'docx' | 'markdown' | 'text' | 'csv'

export interface AgenticDocumentDownload {
  title: string
  filename: string
  format: AgenticDocumentFormat
  content: string
}

export async function sendAgenticChat(
  clientId: string,
  message: string,
  history: AgenticChatTurn[],
  conversationId?: string | null,
  runtime: AgenticRuntime = 'auto',
): Promise<AgenticChatResponse> {
  const { data } = await apiClient.post<AgenticChatResponse>(
    `/clients/${clientId}/agentic-ai/chat`,
    { message, history, conversation_id: conversationId ?? null, runtime },
    { timeout: 120_000 },
  )
  return data
}

export async function streamAgenticChat(
  clientId: string,
  message: string,
  history: AgenticChatTurn[],
  conversationId: string | null | undefined,
  runtime: AgenticRuntime,
  handlers: AgenticStreamHandlers,
): Promise<AgenticChatStreamResult> {
  const { onDelta, onThinking, onTool } = handlers
  let consumedLength = 0
  let pendingLine = ''
  let result: AgenticChatStreamResult | null = null
  let streamError: Error | null = null

  function consume(responseText: string) {
    if (responseText.length < consumedLength) {
      consumedLength = 0
      pendingLine = ''
    }
    pendingLine += responseText.slice(consumedLength)
    consumedLength = responseText.length

    const lines = pendingLine.split('\n')
    pendingLine = lines.pop() ?? ''
    for (const line of lines) {
      if (!line.trim()) continue
      try {
        const event = JSON.parse(line) as AgenticChatStreamEvent
        if (event.type === 'delta') onDelta(event.text)
        else if (event.type === 'thinking') onThinking?.(event.text)
        else if (event.type === 'tool') onTool?.({ name: event.name, label: event.label })
        else if (event.type === 'done') result = event
        else streamError = new Error(event.message)
      } catch {
        streamError = new Error('รูปแบบข้อมูล streaming จาก AI ไม่ถูกต้อง')
      }
    }
  }

  const response = await authenticatedFetch(`/clients/${clientId}/agentic-ai/chat/stream`, {
    method: 'POST',
    body: JSON.stringify({ message, history, conversation_id: conversationId ?? null, runtime }),
  })

  if (!response.body) throw new Error('Browser ไม่รองรับ AI streaming response')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let responseText = ''
  let streamDone = false
  while (!streamDone) {
    const { done, value } = await reader.read()
    streamDone = done
    if (done) continue
    responseText += decoder.decode(value, { stream: true })
    consume(responseText)
  }
  responseText += decoder.decode()
  consume(responseText)

  if (pendingLine.trim()) {
    consume(`${responseText}\n`)
  }
  if (streamError) throw streamError
  if (!result) throw new Error('AI stream จบลงก่อนส่งข้อมูลครบถ้วน')
  return result
}

export async function listAgenticConversations(
  clientId: string,
): Promise<AgenticConversationSummary[]> {
  const { data } = await apiClient.get<AgenticConversationSummary[]>(
    `/clients/${clientId}/agentic-ai/conversations`,
  )
  return data
}

export async function getAgenticConversation(
  clientId: string,
  conversationId: string,
): Promise<AgenticConversation> {
  const { data } = await apiClient.get<AgenticConversation>(
    `/clients/${clientId}/agentic-ai/conversations/${conversationId}`,
  )
  return data
}

export async function deleteAgenticConversation(
  clientId: string,
  conversationId: string,
): Promise<void> {
  await apiClient.delete(`/clients/${clientId}/agentic-ai/conversations/${conversationId}`)
}

export async function downloadAgenticDocument(
  clientId: string,
  document: AgenticDocumentDownload,
): Promise<void> {
  const response = await authenticatedFetch(`/clients/${clientId}/agentic-ai/documents/render`, {
    method: 'POST',
    body: JSON.stringify(document),
  })
  const blob = await response.blob()
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  let filename = document.filename
  if (encodedName) {
    try {
      filename = decodeURIComponent(encodedName)
    } catch {
      // Keep the artifact filename if a proxy rewrites the header incorrectly.
    }
  }

  const url = window.URL.createObjectURL(blob)
  const anchor = window.document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.style.display = 'none'
  window.document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.URL.revokeObjectURL(url)
}
